<template>
  <modal
    size="xl"
    :show="show"
    :hide-footer="true"
    :show-close-icon="false"
    :minimization-config="minimizationConfig"
    :modal-data="modalData"
    :scrollable="false"
    modal-class="idea-modal"
    enforce-focus-on=".cke_dialog_ui_input_text"
    @hidden="$emit('hidden', false)"
  >
    <div class="d-flex align-items-center" slot="minimizedView">
      <i class="idea-icon flex-shrink-0"></i>
      <span class="ml-2 text-truncate">{{ currentIdea.title }}</span>
    </div>
    <div class="tip" v-if="showTip">
      <with-close-icon @close="dismissTip" class="text-prime" dismiss-title="Dismiss">
        <span class="mr-2">TIP: Use &lt; &gt; arrows to move between ideas</span>
      </with-close-icon>
    </div>
    <div class="content">
      <idea-row
        :key="currentIdea.id"
        :orgId="currentIdea.organizationId"
        :listId="currentIdea.listId"
        :idea="currentIdea"
        view-type="details"
        :draggable="false"
        :expand-on-load="true"
        :show-details-page-navigator="false"
        :can-toggle-expansion="false"
        :hide-options="true"
        :hide-comment-icon="true"
        :auto-show-action-icons="true"
        @update-idea="onIdeaUpdate"
      ></idea-row>
      <div class="activity">
        <div class="sub-heading d-flex align-items-center bold">
          <i class="comment-icon"></i>
          <span class="ml-2 text-medium">Idea Discussion</span>
        </div>
        <idea-discussion :focus-add-comment-on-load="focusAddCommentOnLoad" :idea="currentIdea" />
      </div>
    </div>
  </modal>
</template>

<script>
import IdeaDiscussion from '@/components/ideas/IdeaDiscussion'
import { ENTITY_TYPE } from '@/constants/entities'
import { STORAGE_KEYS } from '@/constants/storage'
import localStorage from '@/utils/localStorage'
import * as R from 'ramda'
import Modal from '@/components/Modal'
import WithCloseIcon from '@/components/WithCloseIcon'
import * as keymapService from '@/services/keymapService/keymapService'

/**
 * This component shows idea in a Modal view
 * It shows below informations
 * - IdeaRow in expanded view
 * - Idea comments
 * - Ability to add a comment
 *
 * Props:
 * - show
 * - idea
 *
 * @emits
 * - update
 */
export default {
  name: 'IdeaModal',
  props: ['show', 'idea', 'focusAddCommentOnLoad', 'ideas', 'modalData'],
  components: {
    IdeaRow: () => import('@/components/ideas/IdeaRow/IdeaRow'),
    Modal,
    IdeaDiscussion,
    WithCloseIcon
  },
  model: {
    prop: 'show'
  },
  data() {
    const tipDismissed = localStorage.getItem(STORAGE_KEYS.ideaModalTipDismissed)
    return {
      currentIdea: this.idea,
      showTip: !tipDismissed,
      minimizationConfig: {
        type: ENTITY_TYPE.IDEA
      }
    }
  },
  methods: {
    onNext() {
      const currentIndex = this.ideas.findIndex(R.propEq('id', this.currentIdea.id))
      if (currentIndex > -1 && currentIndex < this.ideas.length - 1) {
        this.currentIdea = this.ideas[currentIndex + 1]
      }
    },
    onPrevious() {
      const currentIndex = this.ideas.findIndex(R.propEq('id', this.currentIdea.id))
      if (currentIndex > 0) {
        this.currentIdea = this.ideas[currentIndex - 1]
      }
    },
    onIdeaUpdate(idea) {
      this.currentIdea = idea
    },
    dismissTip() {
      localStorage.setItem(STORAGE_KEYS.ideaModalTipDismissed, true)
      this.showTip = false
    }
  },
  mounted() {
    if (this.ideas) {
      keymapService.activateCategory('ideaModal', {
        'previous-idea': {
          alwaysExecute: true,
          callback: this.onPrevious
        },
        'next-idea': {
          alwaysExecute: true,
          callback: this.onNext
        }
      })
    }
  },
  beforeDestroy() {
    keymapService.deactivateCategory('ideaModal')
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.idea-row /deep/ .cell {
  border-top: none;
}
/deep/ .note-wrapper {
  background-color: $documentColor;
}
.content {
  min-height: 400px;
  margin-top: 18px;
}
.comment-icon {
  @include dimension(14px, 14px);
  background-size: contain;
}
.activity {
  margin-left: 40px;
  margin-right: 28px;
  border-top: 1px solid $borderPrimary;
}
.sub-heading {
  padding-top: 14px;
  padding-bottom: 14px;
}
.tip {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: $notificationBG;
  border-radius: 10px;
  padding: 3px 3px 3px 12px;
  z-index: 1;
  /deep/ .close-icon {
    opacity: 1;
  }
}
@include media-breakpoint-down(md) {
  .tip {
    display: none;
  }
  .activity {
    margin: 0;
  }
}
</style>
<style lang="scss">
@import '~@/styles/_styleConfigurations.scss';

$sideGutter: 110px;

.idea-modal .modal-top-actions {
  right: 10px;
  top: 14px;
}
.idea-modal .modal-header {
  padding: 0;
}
@include media-breakpoint-down(md) {
  .idea-modal .idea-row {
    border-top: none !important;
    .note-section {
      margin: 0 20px 0 0;
    }
    .summary,
    > .note {
      margin-left: 0 !important;
    }
  }
}

@include media-breakpoint-up(xl) {
  .idea-modal .modal-body {
    padding: 10px $sideGutter 10px $sideGutter;
  }
}
</style>
